import React, { type HTMLAttributes, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductListItemModel } from 'autogen/swagger/all';
import { useProductListItemsByProductIdsQuery } from 'autogen/swagger/all/hooks';
import type { ProductCarouselPreMetadata } from 'autogen/swagger/comai';
import { AskMoreAboutProduct } from 'autogen/translation-keys/trans-website-comai';
import Divider from 'components/Divider';
import { Typography } from 'components/generic/typography';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import HorizontalProductItem from 'components/shared/productItem/components/HorizontalProductItem/HorizontalProductItem';
import useComAIStore from '../store/comAIStore';
import BasicChips from './BasicChips';
import Chip from './Chip';
import Dropdown from './Dropdown';

type ProductChipsProps = HTMLAttributes<HTMLDivElement> & Pick<ProductCarouselPreMetadata, 'products'>;

export default function ProductList(props: ProductChipsProps) {
    const { products, className } = props;
    const { t } = useTranslation();
    const setAskMoreProduct = useComAIStore((state) => state.setAskMoreProduct);
    const setProductListLoading = useComAIStore((state) => state.setProductListLoading);

    const {
        data: productItemData,
        isFetching,
        isError
    } = useProductListItemsByProductIdsQuery(
        {
            products: products.map((c) => Number(c.id))
        },
        { refetchOnWindowFocus: false }
    );

    useEffect(() => {
        setProductListLoading(isFetching);
    }, [setProductListLoading, isFetching]);

    // If there are no productIds from the AI, do not render the component. The AI should tell the user there are no products instead.
    if (!products.length || !productItemData || !productItemData.length) {
        return null;
    }

    // If there is an error fetching product data, we stop the loading state and throw an error to be caught by the ErrorBoundary
    if (isError) {
        setProductListLoading(false);
        throw new Error('No product data found for ProductList component');
    }

    return (
        <FlexColumn className={className}>
            {products.map((product, index) => {
                const { id, short_description, why_perfect_for_you } = product;
                const productChips = product.chips;
                const productItemModel = productItemData?.find(
                    (p) => p.$type === 'ProductListItemModel' && p.id === Number(id)
                ) as ProductListItemModel;

                if (!productItemModel) {
                    return null;
                }

                return (
                    <React.Fragment key={id}>
                        {index > 0 && <Divider />}
                        <FlexColumn>
                            <Typography.Headline size="S">{short_description}</Typography.Headline>
                            <HorizontalProductItem model={productItemModel} showRating />
                            <Typography.Body size="S">{why_perfect_for_you}</Typography.Body>
                            {productChips?.length && (
                                <Dropdown>
                                    <BasicChips chips={productChips} context={{ product_id: id }}>
                                        <Chip onClick={() => setAskMoreProduct(productItemModel)}>
                                            {t(AskMoreAboutProduct)}
                                        </Chip>
                                    </BasicChips>
                                </Dropdown>
                            )}
                        </FlexColumn>
                    </React.Fragment>
                );
            })}
        </FlexColumn>
    );
}
